import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JsonApiBody, JsonApiIndexResponse, JsonApiResponse } from '@app/interfaces';
import { Drive } from '@app/models/documents/directory/drive.model';
import { Node } from '@app/models/documents/directory/node.model';
import { DriveTypeEnum } from '@app/modules/documents-v2/enums/drive-type.enum';
import { NodeInTreeFormat } from '@app/modules/documents-v2/interfaces/node-in-tree-format.interface';
import { environment } from '@env/environment';

type ChildNodesInTreeFormatAPIResponse = {
    data: NodeInTreeFormat[];
};

@Injectable({
    providedIn: 'root',
})
export class DocumentFoldersService {
    constructor(private http: HttpClient) {}

    getDriveBasedOnNode(node: Node): Promise<Drive> {
        return this.getDriveBasedOnNodeId(node.id);
    }

    getDriveBasedOnNodeId(nodeId: number): Promise<Drive> {
        const url = this.getDriveEndpoint(nodeId);

        return this.http
            .get(url)
            .toPromise()
            .then((res: JsonApiResponse<Drive>) => {
                return new Drive(res.data.attributes);
            });
    }

    getNodeAncestors(nodeId: number): Promise<Node[]> {
        const url = this.getAncestorsEndpoint(nodeId);

        return this.http
            .get<JsonApiIndexResponse<JsonApiBody<Node>>>(url)
            .toPromise()
            .then((res) => res.data.map((d) => new Node(d.attributes)));
    }

    getChildrenInTreeFormat(node: Node): Promise<NodeInTreeFormat[]> {
        const url = this.getChildrenInTreeFormatEndpoint(node);

        return this.http
            .get(url)
            .toPromise()
            .then((res: ChildNodesInTreeFormatAPIResponse) => {
                // TODO:: Look into why it's only happening for public drive sometimes
                if (typeof res.data === 'object') {
                    return Object.values(res.data);
                } else {
                    return res.data;
                }
            });
    }

    moveNode(nodesToMove: number[], newParentNode: number): Promise<Object> {
        const url = this.getMoveNodeEndpoint(newParentNode);

        const payload = {
            data: {
                attach: {
                    node_id: nodesToMove,
                },
            },
        };

        return this.http.post(url, payload).toPromise();
    }
    /**
     * Are nodes being moved from private to public? If so, we will warn the user.
     * Currently this hits the API for _every_ node, to get its drive. Ideally we would batch these or get earlier on.
     * However - this is only done on node move and should be quick - only an issue if moving 25 items.
     * TODO: Review this to look for a more performant solution.
     */
    async isMoveMakingNodesPublic(nodeIds: number[], targetNodeId: number): Promise<boolean> {
        const targetDrive = await this.getDriveBasedOnNodeId(targetNodeId);
        /**
         * Get the drives for these nodes
         */
        const drivePromises = nodeIds.map((id) => this.getDriveBasedOnNodeId(id));

        return Promise.all(drivePromises).then((drives: Drive[]) => {
            const driveResults = drives.map((drive: Drive) => {
                return drive.type === DriveTypeEnum.private && targetDrive.type === DriveTypeEnum.public;
            });

            if (driveResults.filter(Boolean).length) {
                return true;
            }

            return false;
        });
    }

    /**
     * Are nodes being moved from public to private?
     */
    async isMoveMakingNodesPrivate(nodeIds: number[], targetNodeId: number): Promise<boolean> {
        const targetDrive = await this.getDriveBasedOnNodeId(targetNodeId);
        /**
         * Get the drives for these nodes
         */
        const drivePromises = nodeIds.map((id) => this.getDriveBasedOnNodeId(id));

        return Promise.all(drivePromises).then((drives: Drive[]) => {
            const driveResults = drives.map((drive: Drive) => {
                return drive.type === DriveTypeEnum.public && targetDrive.type === DriveTypeEnum.private;
            });

            if (driveResults.filter(Boolean).length) {
                return true;
            }

            return false;
        });
    }

    private getDriveEndpoint(nodeId: number): string {
        return environment.api + `/v2/directories/nodes/${nodeId}/drive`;
    }

    private getAncestorsEndpoint(nodeId: number): string {
        return environment.api + `/v2/directories/nodes/${nodeId}/ancestors`;
    }

    private getChildrenInTreeFormatEndpoint(node: Node): string {
        return environment.api + `/v2/directories/nodes/${node.id}/children/tree`;
    }

    private getMoveNodeEndpoint(newParentNode: number): string {
        return environment.api + `/v2/directories/nodes/${newParentNode}/children`;
    }
}
